import React, { useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  CustomTextInput,
  CustomSectionTitle,
  CustomSingleSelect,
  CustomCalendarInput,
  CustomTwoColInputGroup,
} from "@ippon/shared-components";
import type { UseFormReturn } from "react-hook-form";
import {
  calculateContractMonthlyAmount,
  calculateTotalTsuboUnitPrice,
  calculateTsuboUnitPrice,
} from "@ippon/shared-utils";
import { useTsuboLinkedAmounts } from "@/hooks/useTsuboLinkedAmounts";
import type { UnitFormValues } from "../UnitTabForm";

interface Props {
  form: UseFormReturn<UnitFormValues>;
  /** 駐車場系で車両情報も入力させたい時 */
  showVehicleFields?: boolean;
  /** 附帯収益区画 (UI 出し分けの予約。現状未使用) */
  isAncillary?: boolean;
}

const CONTRACT_FORM_OPTIONS = [
  { value: "普通借", label: "普通借" },
  { value: "定期借", label: "定期借" },
  { value: "その他", label: "その他" },
];

const hasInputValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

const ContractSection: React.FC<Props> = ({
  form,
  showVehicleFields,
  isAncillary = false,
}) => {
  const { register, control, watch, setValue, formState } = form;
  const contractorType = watch("contract.contractor_type") || "individual";
  const areaType = watch("area_type");
  const areaTsubo = Number(
    areaType === "common"
      ? watch("common_detail.area_tsubo")
      : watch("private_detail.area_tsubo"),
  );
  const monthlyRent = watch("budget.expected_rent");
  const maintenanceFee = watch("budget.common_fee");
  const rentUnitPrice = watch("budget.rent_unit_price");
  const maintenanceFeeUnitPrice = watch("budget.maintenance_fee_unit_price");
  const pricingType = watch("budget.pricing_type") || "fixed";
  const ancillaryUnitPrice = watch("budget.unit_price");
  const ancillaryQuantity = watch("budget.quantity");
  useEffect(() => {
    if (!isAncillary || pricingType !== "unit_quantity") return;
    setValue(
      "budget.expected_rent",
      calculateContractMonthlyAmount(ancillaryUnitPrice, ancillaryQuantity),
      {
        shouldDirty:
          form.getFieldState("budget.unit_price").isDirty ||
          form.getFieldState("budget.quantity").isDirty,
        shouldValidate: true,
      },
    );
  }, [
    ancillaryQuantity,
    ancillaryUnitPrice,
    form,
    isAncillary,
    pricingType,
    setValue,
  ]);

  useEffect(() => {
    if (isAncillary || areaTsubo <= 0) return;

    if (hasInputValue(monthlyRent) && !hasInputValue(rentUnitPrice)) {
      setValue(
        "budget.rent_unit_price",
        calculateTsuboUnitPrice(Number(monthlyRent), areaTsubo),
        { shouldDirty: false, shouldValidate: true },
      );
    }

    if (
      hasInputValue(maintenanceFee) &&
      !hasInputValue(maintenanceFeeUnitPrice)
    ) {
      setValue(
        "budget.maintenance_fee_unit_price",
        calculateTsuboUnitPrice(Number(maintenanceFee), areaTsubo),
        { shouldDirty: false, shouldValidate: true },
      );
    }
  }, [
    areaTsubo,
    isAncillary,
    maintenanceFee,
    maintenanceFeeUnitPrice,
    monthlyRent,
    rentUnitPrice,
    setValue,
  ]);
  useTsuboLinkedAmounts({
    form,
    areaTsubo,
    monthlyField: "budget.expected_rent",
    unitPriceField: "budget.rent_unit_price",
    enabled: !isAncillary,
  });
  useTsuboLinkedAmounts({
    form,
    areaTsubo,
    monthlyField: "budget.common_fee",
    unitPriceField: "budget.maintenance_fee_unit_price",
    enabled: !isAncillary,
  });
  const totalUnitPrice = calculateTotalTsuboUnitPrice(
    Number(watch("budget.rent_unit_price")) || undefined,
    Number(watch("budget.maintenance_fee_unit_price")) || undefined,
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="契約者情報" size="small" />
      </Box>
      <Stack spacing={0}>
        {/* 契約者種別 — 新規契約モーダルと同じトグル風 (個人 / 法人) */}
        <Box sx={{ mb: 1 }}>
          <Typography fontSize={12} fontWeight={700} mb={0.5}>
            契約者種別
          </Typography>
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              p: 0.5,
              borderRadius: 3,
              bgcolor: "#F1F3F5",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 2.5,
                top: 4,
                bottom: 4,
                width: "calc((100% - 16px) / 2)",
                transform:
                  contractorType === "individual"
                    ? "translateX(0)"
                    : "translateX(calc(100% + 8px))",
                transition: "transform 0.2s ease",
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: "0px 1px 2px rgba(16,24,40,0.08)",
                border: "1px solid rgba(0,0,0,0.1)",
                pointerEvents: "none",
              }}
            />
            {(["individual", "corporate"] as const).map((t) => (
              <Box
                key={t}
                onClick={() =>
                  setValue("contract.contractor_type", t, {
                    shouldDirty: true,
                  })
                }
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  px: 4.5,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: "pointer",
                  color: contractorType === t ? "#111" : "#6B7280",
                  zIndex: 1,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {t === "individual" ? "個人" : "法人"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {contractorType === "individual" && (
          <>
            <CustomTwoColInputGroup
              label="契約者氏名"
              firstName="contract.last_name"
              lastName="contract.first_name"
              placeholderOne="氏"
              placeholderTwo="名"
              register={register as any}
              errors={formState.errors as any}
              isRequired={false}
              marginBottom="0px"
              inputWidth="100%"
              inputWidthSp="100%"
            />
            <CustomTextInput
              label="電話番号"
              name="contract.phone"
              type="tel"
              placeholder="例: 09012345678"
              register={register}
              errors={formState.errors}
              inputMode="tel"
            />
            <CustomTextInput
              label="メールアドレス"
              name="contract.email"
              type="email"
              placeholder="例: yamada@example.com"
              register={register}
              errors={formState.errors}
            />
          </>
        )}

        {contractorType === "corporate" && (
          <>
            <CustomTextInput
              label="法人名"
              name="contract.company_name"
              placeholder="例: 株式会社○○"
              register={register}
              errors={formState.errors}
            />
            <CustomTwoColInputGroup
              label="代表者氏名"
              firstName="contract.representative_last_name"
              lastName="contract.representative_first_name"
              placeholderOne="氏"
              placeholderTwo="名"
              register={register as any}
              errors={formState.errors as any}
              isRequired={false}
              marginBottom="0px"
              inputWidth="100%"
              inputWidthSp="100%"
            />
            <CustomTextInput
              label="電話番号"
              name="contract.phone"
              type="tel"
              placeholder="例: 0312345678"
              register={register}
              errors={formState.errors}
              inputMode="tel"
            />
            <CustomTextInput
              label="メールアドレス"
              name="contract.email"
              type="email"
              placeholder="例: info@company.com"
              register={register}
              errors={formState.errors}
            />
          </>
        )}

        <CustomCalendarInput
          name="budget.start_date"
          control={control as any}
          label="契約開始日"
        />
        <CustomCalendarInput
          name="budget.end_date"
          control={control as any}
          label="契約終了日"
        />
        <CustomSingleSelect
          label="契約形態"
          name="budget.contract_form"
          control={control}
          errors={formState.errors}
          options={CONTRACT_FORM_OPTIONS}
        />
        <CustomCalendarInput
          name="budget.rent_start_date"
          control={control as any}
          label="賃料起算日"
        />
        {isAncillary && (
          <>
            <CustomSingleSelect
              label="料金体系"
              name="budget.pricing_type"
              control={control}
              errors={formState.errors}
              options={[
                { value: "fixed", label: "固定月額" },
                { value: "unit_quantity", label: "単価 × 数量" },
              ]}
            />
            {pricingType === "unit_quantity" && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <CustomTextInput
                  label="単価（税別）"
                  name="budget.unit_price"
                  type="number"
                  showYen
                  watch={watch}
                  setValue={setValue}
                  register={register}
                  errors={formState.errors}
                  inputWidth="100%"
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 120px",
                    gap: 1,
                  }}
                >
                  <CustomTextInput
                    label="数量"
                    name="budget.quantity"
                    type="number"
                    register={register}
                    errors={formState.errors}
                    inputWidth="100%"
                  />
                  <CustomSingleSelect
                    label="単位"
                    name="budget.quantity_unit"
                    control={control}
                    errors={formState.errors}
                    options={[
                      { value: "台", label: "台" },
                      { value: "ヶ所", label: "ヶ所" },
                      { value: "基", label: "基" },
                      { value: "一式", label: "一式" },
                    ]}
                    inputWidth="100%"
                  />
                </Box>
              </Box>
            )}
          </>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: isAncillary ? "1fr" : "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <CustomTextInput
            label="月額賃料(税別)"
            name="budget.expected_rent"
            type="number"
            showYen
            watch={watch}
            setValue={setValue}
            placeholder="例: 150000"
            register={register}
            errors={formState.errors}
            endAdornmentText="円"
            inputWidth="100%"
            readOnly={isAncillary && pricingType === "unit_quantity"}
          />
          {!isAncillary && (
            <CustomTextInput
              label="賃料坪単価"
              name="budget.rent_unit_price"
              type="number"
              showYen
              watch={watch}
              setValue={setValue}
              register={register}
              errors={formState.errors}
              endAdornmentText="円/坪"
              disabled={areaTsubo <= 0}
              inputWidth="100%"
            />
          )}
        </Box>
        {!isAncillary && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <CustomTextInput
                label="共益費"
                name="budget.common_fee"
                type="number"
                showYen
                watch={watch}
                setValue={setValue}
                placeholder="例: 10000"
                register={register}
                errors={formState.errors}
                endAdornmentText="円"
                inputWidth="100%"
              />
              <CustomTextInput
                label="共益費坪単価"
                name="budget.maintenance_fee_unit_price"
                type="number"
                showYen
                watch={watch}
                setValue={setValue}
                register={register}
                errors={formState.errors}
                endAdornmentText="円/坪"
                disabled={areaTsubo <= 0}
                inputWidth="100%"
              />
            </Box>
            <Typography fontSize={12} sx={{ mb: 2 }}>
              賃料合計坪単価:{" "}
              {totalUnitPrice === undefined
                ? "-"
                : `${totalUnitPrice.toLocaleString("ja-JP")}円/坪`}
            </Typography>
          </>
        )}
        {isAncillary && (
          <CustomTextInput
            label="共益費"
            name="budget.common_fee"
            type="number"
            showYen
            watch={watch}
            setValue={setValue}
            placeholder="例: 10000"
            register={register}
            errors={formState.errors}
            endAdornmentText="円"
          />
        )}
        <CustomTextInput
          label="敷金"
          name="budget.deposit_amount"
          type="number"
          showYen
          watch={watch}
          setValue={setValue}
          placeholder="例: 300000"
          register={register}
          errors={formState.errors}
          endAdornmentText="円"
        />
        <CustomTextInput
          label="償却後敷金"
          name="budget.deposit_after_amount"
          type="number"
          showYen
          watch={watch}
          setValue={setValue}
          placeholder="例: 100000"
          register={register}
          errors={formState.errors}
          endAdornmentText="円"
        />
        <CustomTextInput
          label="礼金"
          name="budget.key_money_amount"
          type="number"
          showYen
          watch={watch}
          setValue={setValue}
          placeholder="例: 150000"
          register={register}
          errors={formState.errors}
          endAdornmentText="円"
        />
        <CustomTextInput
          label="備考"
          name="contract.note"
          register={register}
          errors={formState.errors}
          multiline
          rows={3}
          placeholder="契約に関する特記事項があれば記入してください"
        />

        {showVehicleFields && (
          <>
            <CustomTextInput
              label="車両 メーカー"
              name="contract.vehicle.manufacturer"
              register={register}
              errors={formState.errors}
            />
            <CustomTextInput
              label="車両 型式"
              name="contract.vehicle.model"
              register={register}
              errors={formState.errors}
            />
            <CustomTextInput
              label="ナンバー"
              name="contract.vehicle.plate"
              register={register}
              errors={formState.errors}
            />
            <CustomTextInput
              label="色"
              name="contract.vehicle.color"
              register={register}
              errors={formState.errors}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ContractSection;
